import React from 'react'
import styled from "styled-components";
import { marked } from 'marked';
import { device } from "../../utils";
import theme from "../../utils/theme";
import ItemProperties from './ItemProperties';
import LuxWeb3Context from '../../context/LuxWeb3Context';
import CenterEye from '../TheEye/CenterEye';


// ~~~~ STYLING : terminal spec-sheet — bracketed section labels, dim
// uppercase keys against bright values, thin dividers (ghostline style)
const ItemDescriptionWrap = styled.div`
  position: relative;
  padding: 0 10px 75px;
  text-align: left;

  /* Desktop shows this inline next to the media — no overlaid buttons
     below, so no need for the big bottom clearance */
  @media ${device.md} {
    padding: 0 0 15px 5px;
  }

  p {
    font-weight: bold;
    font-size: 14px;
    @media (max-width: 767px) { font-size: 12px; }
    line-height: 24px;
    margin-top: 20px;
    margin-bottom: 5px;
  }

  /* PANE TITLE : the item's name heads the spec pane on desktop, where
     the floating item-header box above the card is hidden. Inverted —
     filled with the pane's palette color, black text, for intensity */
  .pane-title {
    display: none;

    @media ${device.md} {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      /* Pinned to the top of the scrolling pane — content slides under
         it (needs the opaque fill below to mask what passes beneath) */
      position: sticky;
      top: 0;
      z-index: 2;
      font-family: 'pixel9';
      font-size: 18px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding: 3px 10px 5px;
      margin-bottom: 12px;
      background: ${theme.cultPalette[1]};
      color: #000;
      text-shadow: none;
    }

    .pane-title-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .pane-title-eye {
      flex: 0 0 auto;
      transform: none;
      width: 50px;

      // HID IT FOR NOW WAS A BIT TOO BUSY
      display: none;
    }
  }

  /* SECTION LABELS : [ SPEC_SHEET.DATA ] style dividers */
  .spec-label {
    font-family: 'pixel9';
    font-size: 14px;
    @media (max-width: 767px) { font-size: 12px; }
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #ffffff;
    padding-bottom: 4px;
    margin: 18px 0 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    &:first-child {
      margin-top: 0;
    }

    /* The spec table right below brings its own frame — skip the
       underline on this one label to avoid stacked lines */
    &.no-underline {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .product-lore,
  .product-description {
    font-size: 14px;
    @media (max-width: 767px) { font-size: 12px; }
  }

  /* DESCRIPTION SECTIONS : the Shopify HTML carries three classed blocks
     (.product-lore / .product-containment / .product-info) — style each as
     a distinct section: subtitle as a terminal header, content below.
     Stray top-level <br> separators in the source collapse to nothing;
     the sections' own margins do the spacing. */
  .description-lore-html > br,
  .description-rest-html > br {
    display: none;
  }

  .product-lore,
  .product-containment,
  .product-info {
    margin-top: 24px;

    .description-subtitle {
      font-family: 'pixel9';
      font-size: 14px;
      @media (max-width: 767px) { font-size: 12px; }
      letter-spacing: 2px;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.85);
      padding-bottom: 4px;
      margin-bottom: 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }

    .description-sub-content {
      font-size: 14px;
      @media (max-width: 767px) { font-size: 12px; }
      line-height: 22px;
    }
  }

  .product-lore {
    margin-top: 0;
  }

  /* LORE COLLAPSE : starts clamped to a few lines, fading to black, with
     a bracketed READ MORE toggle; expanded shows everything */
  .lore-wrap {
    position: relative;
    margin-top: 22px;

    &.collapsed .description-lore-html {
      max-height: 160px;
      overflow: hidden;
    }

    .lore-fade {
      display: none;
      position: absolute;
      left: 0;
      right: 0;
      top: 90px;
      height: 70px;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000);
      pointer-events: none;
    }

    &.collapsed .lore-fade {
      display: block;
    }

    .read-more {
      display: inline-block;
      margin-top: 8px;
      padding: 2px 10px;
      font-size: 12px;
      letter-spacing: 1px;
      position: relative;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  /* SPEC TABLE : proper bordered table like ghostline's spec-list —
     outer frame, full-height column divider, hairline row separators,
     the whole box inset a bit from the section label */
  .spec-table {
    margin: 6px 0 0 0;
    border: 1px solid rgba(255, 255, 255, 0.25);
  }

  .prop-item {
    font-size: 14px;
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    padding: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);

    &:last-child {
      border-bottom: none;
    }

    .prop-title {
      flex: 0 0 130px;
      padding: 6px 10px;
      border-right: 1px solid rgba(255, 255, 255, 0.12);
      text-align: left;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.85);
      font-size: 12px;
      @media (max-width: 767px) { font-size: 10px; }
    }

    .prop-value {
      padding: 6px 10px;
      text-align: left;
    }
  }

  hr {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }
`;



class ItemDescription extends React.Component {
  static contextType = LuxWeb3Context;

  constructor(props) {
    super(props);
    this.state = { loreExpanded: false };
    this.toggleLore = this.toggleLore.bind(this);
  }

  toggleLore() {
    this.setState({ loreExpanded: !this.state.loreExpanded });
  }

  render() {
    const { description, productTags, title } = this.props;
    const { loreExpanded } = this.state;

    // EYE : outer ring is normally the cult blue, but when the header
    // itself landed on blue, swap the outer ring to purple so it doesn't
    // blend into the middle ring (which always matches the header)
    const eyeOuterColor = theme.cultPalette[1] === '#13FFED' ? '#950AFF' : '#13FFED';
    const mergedDescription = `${description}`;
    const cleanDescription = mergedDescription.replace(/\[http:\/\/cultofneon.art\]/gi, 'https://cultofneon.art');
    const parsed = marked.parse( cleanDescription );

    // SECTIONS : the Shopify description carries a .product-lore block
    // (with .product-containment nested inside it as part of the story)
    // followed by .product-info — everything before product-info collapses
    // behind the READ MORE together, product info stays always visible.
    // Legacy descriptions without the section markup render as one block.
    const splitAt = parsed.indexOf('<div class="product-info"');
    const hasSections = splitAt > -1;
    const loreHtml = hasSections ? parsed.slice(0, splitAt) : null;
    const restHtml = hasSections ? parsed.slice(splitAt) : null;

// ~~~~ COMPONENT BODY
    return (
      <ItemDescriptionWrap className="cult-panel-colors">
        {/* TITLE : desktop only — replaces the hidden item-header box */}
        { !!title && (
          <div className="pane-title">
            <span className="pane-title-text">{title}</span>
            <CenterEye
              className="pane-title-eye"
              width={24}
              animated={false}
              bodyColor={eyeOuterColor}
              pupilColor={theme.cultPalette[1]}
              irisColor="#000000"
            />
          </div>
        )}

        <div className="spec-label no-underline">[ SPEC_SHEET.DATA ]</div>
        <ItemProperties className="spec-table" productTags={productTags} />

        { hasSections ? (<>
          {/* LORE : collapsed to a teaser, fading out, until expanded */}
          <div className={`lore-wrap ${loreExpanded ? '' : 'collapsed'}`}>
            <div className="description-lore-html" dangerouslySetInnerHTML={{__html: loreHtml}}></div>
            <div className="lore-fade"></div>
            <a
              className="read-more cult-button"
              onClick={this.toggleLore}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') this.toggleLore(); }}
              role="button"
              tabIndex={0}
              aria-expanded={loreExpanded}
            >[ {loreExpanded ? 'COLLAPSE' : 'READ MORE'} ]</a>
          </div>

          {/* CONTAINMENT + PRODUCT INFO */}
          <div className="description-rest-html" dangerouslySetInnerHTML={{__html: restHtml}}></div>
        </>) : (
          <>
            <div className="spec-label">[ INFO.TXT ]</div>
            <div className="product-description" dangerouslySetInnerHTML={{__html: parsed}}></div>
          </>
        )}
      </ItemDescriptionWrap>
    )
  }
};

export default ItemDescription;